/**
 * @fileoverview In-session engagement counters and tier scoring.
 * @description Tracks action counts, high-intent signals, and active tab time
 * in sessionStorage. Feeds `site_action`, `page_view`, `page_engagement`, and
 * `session_engagement` events with comparable engagement dimensions.
 *
 * @module lib/site-intelligence/session-engagement
 */

import {
  CONSENT_CHANGED_EVENT,
  CONSENT_STORAGE_KEY,
  hasExplicitAnalyticsOptOut,
  isAnalyticsStorageAllowed,
} from "~/lib/analytics/consent"

import { classifyEngagementTier, type SessionEngagementTier } from "./engagement-score"
import type { VisitorIntent } from "./intent"
import { peekJourneyProps } from "./session-journey"

function purgeEngagementIfExplicitOptOut(): boolean {
  if (!hasExplicitAnalyticsOptOut()) return false
  resetSessionEngagement()
  return true
}

let optOutPurgeListenersBound = false

/** Flushes in-memory visible-tab elapsed time before reading stored counters. */
let flushActiveTimeCallback: (() => void) | null = null

/**
 * Registers a callback that persists pending active-tab time before engagement reads.
 *
 * @param {() => void} fn - Flushes elapsed visible time into sessionStorage.
 * @returns {() => void} Unregister function for effect cleanup.
 */
export function registerActiveTimeFlusher(fn: () => void): () => void {
  flushActiveTimeCallback = fn
  return (): void => {
    if (flushActiveTimeCallback === fn) {
      flushActiveTimeCallback = null
    }
  }
}

function flushPendingActiveTime(): void {
  flushActiveTimeCallback?.()
}

function bindOptOutPurgeListeners(): void {
  if (optOutPurgeListenersBound || typeof window === "undefined") return
  optOutPurgeListenersBound = true

  const purgeIfOptedOut = (): void => {
    if (hasExplicitAnalyticsOptOut()) {
      resetSessionEngagement()
    }
  }

  window.addEventListener(CONSENT_CHANGED_EVENT, purgeIfOptedOut)
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key === null || event.key === CONSENT_STORAGE_KEY) {
      purgeIfOptedOut()
    }
  })
}

function ensureOptOutPurgeListeners(): void {
  bindOptOutPurgeListeners()
}

/** sessionStorage key for engagement counters. */
export const SESSION_ENGAGEMENT_STORAGE_KEY = "adsights_session_engagement_v1"

/** sessionStorage flag — terminal `session_engagement` emitted once per tab. */
export const SESSION_ENGAGEMENT_EMITTED_KEY = "adsights_session_engagement_emitted_v1"

/** sessionStorage flag — prior document deferred terminal emit for a reload unload. */
export const SESSION_ENGAGEMENT_RELOAD_DEFERRED_KEY =
  "adsights_session_engagement_reload_deferred_v1"

/** Max scroll depth seen this session (hydrated from sessionStorage on reload). */
let sessionMaxScrollPct = 0

interface StoredEngagement {
  action_count: number
  high_intent_action_count: number
  active_time_ms: number
  signal_weight: number
  max_scroll_pct: number
}

/** Props attached to analytics events for engagement context. */
export type SessionEngagementProps = Readonly<{
  session_action_count: number
  session_high_intent_actions: number
  session_active_time_msec: number
  session_engagement_tier: SessionEngagementTier
}>

function emptyEngagement(): StoredEngagement {
  return {
    action_count: 0,
    high_intent_action_count: 0,
    active_time_ms: 0,
    signal_weight: 0,
    max_scroll_pct: 0,
  }
}

function hydrateSessionMaxScrollFromStored(stored: StoredEngagement): void {
  if (stored.max_scroll_pct > 0) {
    sessionMaxScrollPct = Math.max(sessionMaxScrollPct, stored.max_scroll_pct)
  }
}

function readSessionMaxScrollPct(): number {
  hydrateSessionMaxScrollFromStored(readStored())
  return sessionMaxScrollPct
}

function readStored(): StoredEngagement {
  try {
    const raw = sessionStorage.getItem(SESSION_ENGAGEMENT_STORAGE_KEY)
    if (!raw) return emptyEngagement()
    const parsed = JSON.parse(raw) as Partial<StoredEngagement>
    const result: StoredEngagement = {
      action_count:
        typeof parsed.action_count === "number" && Number.isFinite(parsed.action_count)
          ? Math.max(0, Math.floor(parsed.action_count))
          : 0,
      high_intent_action_count:
        typeof parsed.high_intent_action_count === "number" &&
        Number.isFinite(parsed.high_intent_action_count)
          ? Math.max(0, Math.floor(parsed.high_intent_action_count))
          : 0,
      active_time_ms:
        typeof parsed.active_time_ms === "number" && Number.isFinite(parsed.active_time_ms)
          ? Math.max(0, Math.floor(parsed.active_time_ms))
          : 0,
      signal_weight:
        typeof parsed.signal_weight === "number" && Number.isFinite(parsed.signal_weight)
          ? Math.max(0, Math.floor(parsed.signal_weight))
          : 0,
      max_scroll_pct:
        typeof parsed.max_scroll_pct === "number" && Number.isFinite(parsed.max_scroll_pct)
          ? Math.max(0, Math.min(100, Math.round(parsed.max_scroll_pct)))
          : 0,
    }
    hydrateSessionMaxScrollFromStored(result)
    return result
  } catch {
    return emptyEngagement()
  }
}

function writeStored(value: StoredEngagement): void {
  try {
    sessionStorage.setItem(SESSION_ENGAGEMENT_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Quota/unavailable — analytics must never break UX.
  }
}

function tierForStored(stored: StoredEngagement, path: string): SessionEngagementTier {
  const journey = peekJourneyProps(path)
  return classifyEngagementTier({
    pageCount: journey?.session_page_index ?? 1,
    actionCount: stored.action_count,
    signalWeight: stored.signal_weight,
    highIntentActionCount: stored.high_intent_action_count,
    activeTimeMs: stored.active_time_ms,
    maxIntent: journey?.session_max_intent ?? "awareness",
    maxScrollDepthPct: readSessionMaxScrollPct(),
  })
}

function defaultEngagementProps(path: string): SessionEngagementProps {
  const tier = classifyEngagementTier({
    pageCount: 1,
    actionCount: 0,
    signalWeight: 0,
    highIntentActionCount: 0,
    activeTimeMs: 0,
    maxIntent: "awareness",
    maxScrollDepthPct: 0,
  })
  void path
  return {
    session_action_count: 0,
    session_high_intent_actions: 0,
    session_active_time_msec: 0,
    session_engagement_tier: tier,
  }
}

/**
 * Returns current engagement dimensions for analytics events.
 *
 * @param {string} path - Current pathname (for journey cross-read).
 * @returns {SessionEngagementProps} Engagement snapshot.
 */
export function getSessionEngagementProps(path: string): SessionEngagementProps {
  flushPendingActiveTime()
  if (purgeEngagementIfExplicitOptOut()) return defaultEngagementProps(path)
  if (!isAnalyticsStorageAllowed()) return defaultEngagementProps(path)
  const stored = readStored()
  return {
    session_action_count: stored.action_count,
    session_high_intent_actions: stored.high_intent_action_count,
    session_active_time_msec: stored.active_time_ms,
    session_engagement_tier: tierForStored(stored, path),
  }
}

/**
 * Records an explicit site action (CTA click, nav, toggle, etc.).
 *
 * @param {Object} options - Action metadata.
 * @param {boolean} [options.highIntent=false] - Whether this action signals lead/purchase intent.
 * @returns {SessionEngagementProps} Updated engagement snapshot.
 */
export function recordSiteAction(options: { highIntent?: boolean; path: string }): SessionEngagementProps {
  ensureOptOutPurgeListeners()
  if (purgeEngagementIfExplicitOptOut()) return defaultEngagementProps(options.path)
  if (!isAnalyticsStorageAllowed()) return defaultEngagementProps(options.path)

  const stored = readStored()
  const next: StoredEngagement = {
    ...stored,
    action_count: stored.action_count + 1,
    high_intent_action_count:
      stored.high_intent_action_count + (options.highIntent ? 1 : 0),
  }
  writeStored(next)
  return getSessionEngagementProps(options.path)
}

/**
 * Records a weighted engagement signal from automatic event fan-out (scroll,
 * resource_interact, email_capture, etc.).
 *
 * @param {Object} options - Signal metadata.
 * @param {number} options.weight - Engagement weight (1–5).
 * @param {boolean} [options.highIntent=false] - High-intent signal flag.
 * @param {string} options.path - Current pathname.
 * @returns {void}
 */
export function recordEngagementSignal(options: {
  weight: number
  highIntent?: boolean
  /** When false, only signal_weight increments (scroll milestones, etc.). */
  countsAsAction?: boolean
  path: string
}): void {
  ensureOptOutPurgeListeners()
  if (purgeEngagementIfExplicitOptOut()) return
  if (!isAnalyticsStorageAllowed()) return

  const stored = readStored()
  const weight = Math.max(1, Math.min(5, Math.floor(options.weight)))
  const countsAsAction = options.countsAsAction !== false
  const next: StoredEngagement = {
    ...stored,
    signal_weight: stored.signal_weight + weight,
    action_count: stored.action_count + (countsAsAction ? 1 : 0),
    high_intent_action_count:
      stored.high_intent_action_count + (options.highIntent && countsAsAction ? 1 : 0),
  }
  writeStored(next)
}

/**
 * Whether the terminal `session_engagement` event was already emitted this tab.
 *
 * @returns {boolean} True when the tab session summary was sent.
 */
export function hasSessionEngagementBeenEmitted(): boolean {
  try {
    return sessionStorage.getItem(SESSION_ENGAGEMENT_EMITTED_KEY) === "1"
  } catch {
    return false
  }
}

/**
 * Marks the tab session as having emitted `session_engagement` (once per tab).
 *
 * @returns {void}
 */
export function markSessionEngagementEmitted(): void {
  if (!isAnalyticsStorageAllowed()) return
  try {
    sessionStorage.setItem(SESSION_ENGAGEMENT_EMITTED_KEY, "1")
  } catch {
    // Quota/unavailable — analytics must never break UX.
  }
}

/**
 * Clears the terminal-emitted marker for a new document load in the same tab.
 *
 * sessionStorage counters persist across reloads, but each document should be
 * able to emit its own terminal `session_engagement` on tab close.
 *
 * @returns {void}
 */
export function clearSessionEngagementEmittedFlag(): void {
  try {
    sessionStorage.removeItem(SESSION_ENGAGEMENT_EMITTED_KEY)
  } catch {
    // ignore
  }
}

/**
 * Whether the current document load was a reload (same tab, fresh document).
 *
 * @returns {boolean} True when `PerformanceNavigationTiming.type` is `reload`.
 */
export function isReloadNavigation(): boolean {
  if (typeof performance === "undefined") return false
  if (typeof performance.getEntriesByType !== "function") return false
  const entry = performance.getEntriesByType("navigation")[0]
  if (!entry || !("type" in entry)) return false
  return (entry as PerformanceNavigationTiming).type === "reload"
}

/** Set when the visitor initiates a reload before `pagehide` (Navigation API / keyboard). */
let pendingReloadPagehide = false

/**
 * Whether the current `pagehide` is for a reload (skip terminal emit; counters persist).
 *
 * @returns {boolean} True when a reload unload was detected.
 */
export function isReloadPagehideUnload(): boolean {
  return pendingReloadPagehide
}

/**
 * Marks an imminent reload unload so `pagehide` does not emit `session_engagement`.
 *
 * @returns {void}
 */
export function markPendingReloadPagehide(): void {
  pendingReloadPagehide = true
}

/**
 * Records that the outgoing document deferred terminal emit for a reload unload.
 *
 * @returns {void}
 */
export function markSessionEngagementDeferredForReload(): void {
  try {
    sessionStorage.setItem(SESSION_ENGAGEMENT_RELOAD_DEFERRED_KEY, "1")
  } catch {
    // Quota/unavailable — analytics must never break UX.
  }
}

function consumeSessionEngagementReloadDeferred(): boolean {
  try {
    const deferred = sessionStorage.getItem(SESSION_ENGAGEMENT_RELOAD_DEFERRED_KEY) === "1"
    if (deferred) {
      sessionStorage.removeItem(SESSION_ENGAGEMENT_RELOAD_DEFERRED_KEY)
    }
    return deferred
  } catch {
    return false
  }
}

/**
 * Resets reload-unload detection and clears the emitted flag only when the prior
 * document deferred its terminal emit for a detected reload unload.
 *
 * Toolbar/location-bar reloads that already sent `session_engagement` on
 * undetected `pagehide` keep the marker so tab close does not duplicate the
 * same persisted counters.
 *
 * @returns {void}
 */
export function prepareSessionEngagementForDocumentEntry(): void {
  pendingReloadPagehide = false
  readSessionMaxScrollPct()
  if (!isReloadNavigation()) return
  const hadDeferredReload = consumeSessionEngagementReloadDeferred()
  if (hadDeferredReload) {
    clearSessionEngagementEmittedFlag()
  }
}

/**
 * Binds reload detection for `pagehide` (Navigation API + keyboard fallback).
 *
 * @returns {() => void} Cleanup for effect teardown.
 */
export function bindReloadPagehideDetection(): () => void {
  if (typeof window === "undefined") return (): void => undefined

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "F5") {
      markPendingReloadPagehide()
      return
    }
    if (typeof event.key === "string" && event.key.toLowerCase() === "r" && (event.ctrlKey || event.metaKey)) {
      markPendingReloadPagehide()
    }
  }

  window.addEventListener("keydown", onKeyDown)

  type NavigateEventLike = Event & { navigationType?: string }
  const navigation = (window as Window & { navigation?: EventTarget }).navigation
  let onNavigate: ((event: Event) => void) | undefined

  if (navigation && "addEventListener" in navigation) {
    onNavigate = (event: Event): void => {
      const navEvent = event as NavigateEventLike
      if (navEvent.navigationType === "reload") {
        markPendingReloadPagehide()
      }
    }
    navigation.addEventListener("navigate", onNavigate)
  }

  return (): void => {
    window.removeEventListener("keydown", onKeyDown)
    if (navigation && onNavigate && "removeEventListener" in navigation) {
      navigation.removeEventListener("navigate", onNavigate)
    }
  }
}

/**
 * Engagement + journey dimensions for conversion events (`request_access_submit`,
 * `purchase`, etc.) so GA4 filters on session_engagement_tier match conversions.
 *
 * @param {string} path - Current pathname.
 * @returns {Object} Props to merge into conversion event params.
 */
export function getConversionIntelligenceProps(path: string): Readonly<{
  session_action_count: number
  session_engagement_tier: SessionEngagementTier
  session_high_intent_actions: number
  session_page_index?: number
  session_max_intent?: VisitorIntent
}> {
  const engagement = getSessionEngagementProps(path)
  const journey = peekJourneyProps(path)
  return {
    session_action_count: engagement.session_action_count,
    session_engagement_tier: engagement.session_engagement_tier,
    session_high_intent_actions: engagement.session_high_intent_actions,
    ...(journey
      ? {
          session_page_index: journey.session_page_index,
          session_max_intent: journey.session_max_intent,
        }
      : {}),
  }
}

/**
 * Curated intelligence subset for GA4 conversion events that already carry
 * attribution + lead dimensions near the 25-param cap.
 *
 * @param {string} path - Current pathname.
 * @returns {Object} Tier + action count only (full set via {@link getConversionIntelligenceProps}).
 */
export function getConversionIntelligencePropsForGa4(path: string): Readonly<{
  session_action_count: number
  session_engagement_tier: SessionEngagementTier
}> {
  const engagement = getSessionEngagementProps(path)
  return {
    session_action_count: engagement.session_action_count,
    session_engagement_tier: engagement.session_engagement_tier,
  }
}

/**
 * Accumulates visible-tab active time into the session counter.
 *
 * @param {number} deltaMs - Milliseconds to add (must be positive).
 * @returns {void}
 */
export function recordActiveTime(deltaMs: number): void {
  ensureOptOutPurgeListeners()
  if (purgeEngagementIfExplicitOptOut()) return
  if (!isAnalyticsStorageAllowed() || deltaMs <= 0) return
  const stored = readStored()
  writeStored({
    ...stored,
    active_time_ms: stored.active_time_ms + Math.round(deltaMs),
  })
}

/**
 * Updates the session max scroll depth (for tier scoring).
 *
 * @param {number} pct - Scroll depth percent [0–100].
 * @returns {void}
 */
export function updateSessionMaxScroll(pct: number): void {
  if (!Number.isFinite(pct)) return
  if (!isAnalyticsStorageAllowed()) return
  sessionMaxScrollPct = Math.max(sessionMaxScrollPct, Math.min(100, Math.round(pct)))
  const stored = readStored()
  if (sessionMaxScrollPct <= stored.max_scroll_pct) return
  writeStored({ ...stored, max_scroll_pct: sessionMaxScrollPct })
}

/**
 * Returns a full session engagement summary for the terminal `session_engagement` event.
 *
 * @param {string} path - Current pathname.
 * @returns {SessionEngagementProps | null} Summary, or null when analytics denied / empty session.
 */
export function getSessionEngagementSummary(path: string): SessionEngagementProps | null {
  ensureOptOutPurgeListeners()
  if (purgeEngagementIfExplicitOptOut()) return null
  if (!isAnalyticsStorageAllowed()) return null
  const stored = readStored()
  const journey = peekJourneyProps(path)
  const maxScroll = readSessionMaxScrollPct()
  const hasMeaningfulEngagement =
    stored.action_count > 0 ||
    stored.active_time_ms >= 5000 ||
    stored.signal_weight > 0 ||
    maxScroll >= 75 ||
    (journey?.session_page_index ?? 0) > 1
  if (!hasMeaningfulEngagement) {
    return null
  }
  return getSessionEngagementProps(path)
}

/**
 * Clears session engagement state (testing / consent opt-out).
 *
 * @returns {void}
 */
export function resetSessionEngagement(): void {
  sessionMaxScrollPct = 0
  try {
    sessionStorage.removeItem(SESSION_ENGAGEMENT_STORAGE_KEY)
    sessionStorage.removeItem(SESSION_ENGAGEMENT_EMITTED_KEY)
  } catch {
    // ignore
  }
}
